import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, Calendar, Phone, Plus, AlertTriangle, CheckCircle, ChevronRight, Heart, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useAuth } from '../../store/authStore';
import { getSessions, getReadings } from '../../api/sessions';
import { getAlerts } from '../../api/alerts';
import { getAppointments } from '../../api/appointments';
import { SkeletonBlock } from '../../components/SkeletonBlock';
import { Button } from '../../components/Button';
import { InputField } from '../../components/InputField';
import { LaborPhaseEstimator } from './LaborPhaseEstimator';
import { MaternalHeartRateWave } from './MaternalHeartRateWave';
const SYMPTOMS = ['Nausea', 'Fatigue', 'Headache', 'Swelling', 'Heartburn', 'Back Pain', 'Spotting'];
export function MotherScreen() {
  const navigate = useNavigate();
  const {
    user,
    token
  } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [sessionData, setSessionData] = useState<any>(null);
  const [piezoReadings, setPiezoReadings] = useState<any[]>([]);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [nextAppointment, setNextAppointment] = useState<any>(null);
  // Local Storage States
  const [weightLog, setWeightLog] = useState<any[]>(() => {
    const saved = localStorage.getItem('aurora_weight_log');
    return saved ? JSON.parse(saved) : [];
  });
  const [bpLog, setBpLog] = useState<any[]>(() => {
    const saved = localStorage.getItem('aurora_bp_log');
    return saved ? JSON.parse(saved) : [];
  });
  const [mhrLog, setMhrLog] = useState<any[]>(() => {
    const saved = localStorage.getItem('aurora_mhr_log');
    return saved ? JSON.parse(saved) : [];
  });
  const [symptomLog, setSymptomLog] = useState<any[]>(() => {
    const saved = localStorage.getItem('aurora_symptom_log');
    return saved ? JSON.parse(saved) : [];
  });
  // Bottom Sheet States
  const [showWeightSheet, setShowWeightSheet] = useState(false);
  const [weightInput, setWeightInput] = useState('');
  const [weightDate, setWeightDate] = useState(new Date().toISOString().split('T')[0]);
  const [showBpSheet, setShowBpSheet] = useState(false);
  const [bpSysInput, setBpSysInput] = useState('');
  const [bpDiaInput, setBpDiaInput] = useState('');
  const [bpDate, setBpDate] = useState(new Date().toISOString().split('T')[0]);
  const [showMhrSheet, setShowMhrSheet] = useState(false);
  const [mhrInput, setMhrInput] = useState('');
  const [mhrDate, setMhrDate] = useState(new Date().toISOString().split('T')[0]);
  const [mhrTime, setMhrTime] = useState(() => {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  });
  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      try {
        const [sessionsRes, alertsRes, apptsRes] = await Promise.all([getSessions(token), getAlerts(token), getAppointments(token)]);
        if (sessionsRes && sessionsRes.length > 0) {
          const latestSession = sessionsRes[0];
          setSessionData(latestSession);
          if (latestSession.id) {
            const readingsRes = await getReadings(token, latestSession.id, {
              sensorType: 'piezo',
              limit: 100
            });
            if (readingsRes && readingsRes.data) {
              setPiezoReadings(readingsRes.data.reverse());
            }
          }
        }
        if (alertsRes) {
          setAlerts(alertsRes);
        }
        if (apptsRes && apptsRes.length > 0) {
          const upcoming = apptsRes.filter((a: any) => new Date(a.appointmentDate).getTime() >= new Date().getTime()).sort((a: any, b: any) => new Date(a.appointmentDate).getTime() - new Date(b.appointmentDate).getTime());
          if (upcoming.length > 0) {
            setNextAppointment(upcoming[0]);
          }
        }
      } catch (err) {
        // Handle silently
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [token]);
  // Derived Data
  const pregnancyWeek = 24; // Hardcoded for demo, could be derived from user.dueDate
  const trimester = pregnancyWeek < 14 ? '1st' : pregnancyWeek < 28 ? '2nd' : '3rd';
  const hasRecentAlerts = alerts.some(a => a.status === 'active' || a.status === 'resolved' && Date.now() - new Date(a.createdAt).getTime() < 86400000 * 3);
  const contractionData = useMemo(() => {
    if (!piezoReadings.length) return [];
    return piezoReadings.map((r: any, i: number) => ({
      time: new Date(r.timestamp).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      }),
      value: r.value
    }));
  }, [piezoReadings]);
  const lastPeakTime = useMemo(() => {
    if (!piezoReadings.length) return null;
    const peaks = piezoReadings.filter(r => r.value > 2500);
    if (!peaks.length) return null;
    const lastPeak = peaks[peaks.length - 1];
    const diffMins = Math.floor((Date.now() - new Date(lastPeak.timestamp).getTime()) / 60000);
    return diffMins;
  }, [piezoReadings]);
  const weightChartData = useMemo(() => {
    return [...weightLog].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()).map(entry => ({
      date: new Date(entry.date).toLocaleDateString([], {
        month: 'short',
        day: 'numeric'
      }),
      weight: entry.weight
    }));
  }, [weightLog]);
  const mhrChartData = useMemo(() => {
    return [...mhrLog].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()).slice(-30).map(entry => ({
      time: new Date(entry.timestamp).toLocaleDateString([], {
        month: 'short',
        day: 'numeric'
      }),
      bpm: entry.bpm
    }));
  }, [mhrLog]);
  const latestMhr = useMemo(() => {
    if (!mhrLog.length) return null;
    return [...mhrLog].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0];
  }, [mhrLog]);
  const getMhrStatus = (bpm: number) => {
    if (bpm < 60 || bpm > 130) return {
      label: 'High',
      color: 'text-danger',
      bg: 'bg-danger/10',
      dot: 'bg-danger'
    };
    if (bpm > 100) return {
      label: 'Elevated',
      color: 'text-amber-600',
      bg: 'bg-amber-500/15',
      dot: 'bg-amber-500'
    };
    return {
      label: 'Normal',
      color: 'text-accent',
      bg: 'bg-accent/15',
      dot: 'bg-accent'
    };
  };
  const todaySymptoms = useMemo(() => {
    const todayStr = new Date().toDateString();
    return symptomLog.filter(s => new Date(s.timestamp).toDateString() === todayStr);
  }, [symptomLog]);
  // Handlers
  const handleLogSymptom = (symptom: string) => {
    const newLog = [{
      symptom,
      timestamp: new Date().toISOString()
    }, ...symptomLog];
    setSymptomLog(newLog);
    localStorage.setItem('aurora_symptom_log', JSON.stringify(newLog));
  };
  const handleSaveWeight = () => {
    if (!weightInput || !weightDate) return;
    const newEntry = {
      id: Date.now().toString(),
      weight: parseFloat(weightInput),
      date: weightDate
    };
    const newLog = [newEntry, ...weightLog];
    setWeightLog(newLog);
    localStorage.setItem('aurora_weight_log', JSON.stringify(newLog));
    setShowWeightSheet(false);
    setWeightInput('');
  };
  const handleSaveBp = () => {
    if (!bpSysInput || !bpDiaInput || !bpDate) return;
    const newEntry = {
      id: Date.now().toString(),
      systolic: parseInt(bpSysInput, 10),
      diastolic: parseInt(bpDiaInput, 10),
      date: bpDate
    };
    const newLog = [newEntry, ...bpLog];
    setBpLog(newLog);
    localStorage.setItem('aurora_bp_log', JSON.stringify(newLog));
    setShowBpSheet(false);
    setBpSysInput('');
    setBpDiaInput('');
  };
  const handleSaveMhr = () => {
    const bpmVal = parseInt(mhrInput, 10);
    if (!bpmVal || bpmVal < 30 || bpmVal > 220 || !mhrDate || !mhrTime) return;
    const timestamp = new Date(`${mhrDate}T${mhrTime}:00`).toISOString();
    const newEntry = {
      id: Date.now().toString(),
      bpm: bpmVal,
      timestamp
    };
    const newLog = [newEntry, ...mhrLog];
    setMhrLog(newLog);
    localStorage.setItem('aurora_mhr_log', JSON.stringify(newLog));
    setShowMhrSheet(false);
    setMhrInput('');
  };
  return <main className="w-full max-w-[800px] mx-auto pb-20 md:pb-0 animate-fadeInUp space-y-6" data-id="element-1627" data-mp="3ozawj9hemgy5">
      {/* 1. HEADER CARD */}
      <div className="bg-white rounded-[24px] p-6 shadow-card border border-border/50 relative overflow-hidden" data-id="element-1628" data-mp="1ppb50othtpru">
        <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-bl-full -z-0" data-id="element-1629" data-mp="bqomlfbamy4w" />
        <div className="relative z-10 flex justify-between items-start mb-6" data-id="element-1630" data-mp="708bi1uy3olp">
          <div data-id="element-1631" data-mp="6y3xgr3c197p">
            <h1 className="text-[24px] font-semibold text-text-primary mb-1" data-id="element-1632" data-mp="2ymp9pwitxyu">
              {user?.fullName || 'Mother'}
            </h1>
            <p className="text-[15px] text-text-secondary" data-id="element-1633" data-mp="3u5z8eagyslhe">
              Week {pregnancyWeek} · {trimester} Trimester
            </p>
          </div>
          <div className={`px-3 py-1.5 rounded-pill text-[13px] font-medium flex items-center ${hasRecentAlerts ? 'bg-danger/10 text-danger' : 'bg-success/10 text-success'}`} data-id="element-1634" data-mp="ungfmggn0rm5">
            {hasRecentAlerts ? <>
                <AlertTriangle size={14} className="mr-1.5" data-id="element-1635" data-mp="goylo1052ivs" />
                Needs Attention
              </> : <>
                <CheckCircle size={14} className="mr-1.5" data-id="element-1636" data-mp="36cz8ej7ku542" />
                Healthy
              </>}
          </div>
        </div>

        <div className="space-y-2" data-id="element-1637" data-mp="33kt2192raoj7">
          <div className="flex justify-between text-[12px] text-text-secondary font-medium px-1" data-id="element-1638" data-mp="33lvu3kbtex0p">
            <span data-id="element-1639" data-mp="2yocg8lj14m9a">Week 1</span>
            <span data-id="element-1640" data-mp="3dbfgv12nfb0u">Week 40</span>
          </div>
          <div className="w-full h-3 bg-secondary rounded-full overflow-hidden" data-id="element-1641" data-mp="3dc7ai6coqyoh">
            <motion.div initial={{
            width: 0
          }} animate={{
            width: `${pregnancyWeek / 40 * 100}%`
          }} transition={{
            duration: 1,
            ease: 'easeOut'
          }} className="h-full bg-primary rounded-full" data-id="element-1642" data-mp="38jnxpblto7kq" />
          </div>
        </div>
      </div>

      {/* 2. CONTRACTION TRACKER */}
      <div className="bg-white rounded-[24px] p-6 shadow-card border border-border/50" data-id="element-1643" data-mp="3r93xfwponw03">
        <div className="flex items-center justify-between mb-6" data-id="element-1644" data-mp="21win5dvirxn5">
          <div className="flex items-center" data-id="element-1645" data-mp="1wy7c4zthnh3c">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mr-3" data-id="element-1646" data-mp="1wzigb0quiquz">
              <Activity size={20} className="text-primary" data-id="element-1647" data-mp="chhgicvw49q0" />
            </div>
            <h2 className="text-[18px] font-semibold text-text-primary" data-id="element-1648" data-mp="hdexmlez9k3u">
              Contraction Tracker
            </h2>
          </div>
        </div>

        {isLoading ? <SkeletonBlock height="160px" borderRadius="16px" data-id="element-1649" data-mp="7n3omkgfskq3" /> : contractionData.length > 0 ? <>
            <div className="h-[160px] w-full mb-4" data-id="element-1650" data-mp="3psq9bf8kz91d">
              <ResponsiveContainer width="100%" height="100%" data-id="element-1651" data-mp="3tvv754hpumjo">
                <LineChart data={contractionData} data-id="element-1652" data-mp="3tta5afhf46lx">
                  <XAxis dataKey="time" hide data-id="element-1653" data-mp="3fx5atr2q0qwd" />
                  <YAxis hide domain={['dataMin - 100', 'dataMax + 100']} data-id="element-1654" data-mp="3fy6jjow9l1ax" />
                  <Tooltip contentStyle={{
                borderRadius: '12px',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }} data-id="element-1655" data-mp="3kv6ry6gl6g57" />
                  <Line type="monotone" dataKey="value" stroke="#6B21E8" strokeWidth={3} dot={false} activeDot={{
                r: 6,
                fill: '#6B21E8'
              }} data-id="element-1656" data-mp="1hmxtbcoyrupu" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[14px] text-text-secondary text-center" data-id="element-1657" data-mp="20bkv9mbug3z7">
              {lastPeakTime !== null ? `Last peak: ${lastPeakTime}m ago` : 'No significant peaks detected recently'}
            </p>
          </> : <div className="h-[160px] bg-secondary rounded-[16px] flex flex-col items-center justify-center text-center p-4" data-id="element-1658" data-mp="1lo9ijh3xedxi">
            <Activity size={32} className="text-text-secondary/50 mb-2" data-id="element-1659" data-mp="2j4fc6lt12662" />
            <p className="text-[15px] font-medium text-text-primary mb-1" data-id="element-1660" data-mp="2ecxafvj6k1k1">
              No recent data
            </p>
            <p className="text-[13px] text-text-secondary max-w-[200px]" data-id="element-1661" data-mp="2mm33mfa570e2">
              Start a monitoring session to track contractions.
            </p>
          </div>}
      </div>

      {/* 3. LABOR PHASE & DILATION ESTIMATOR */}
      <LaborPhaseEstimator token={token} data-id="element-1662" data-mp="2vkojg2o8c1b5" />

      {/* 3.5 MATERNAL HEART RATE */}
      <div className="bg-white rounded-[24px] p-6 shadow-card border border-border/50" data-id="element-1663" data-mp="30mnkv70eso5g">
        <div className="flex items-center justify-between mb-5" data-id="element-1664" data-mp="35jdzrr0h9dpa">
          <div className="flex items-center" data-id="element-1665" data-mp="39liocbc2jk52">
            <div className="w-10 h-10 rounded-full bg-accent/15 flex items-center justify-center mr-3" data-id="element-1666" data-mp="39k7ounpnoyjr">
              <Heart size={20} className="text-accent" data-id="element-1667" data-mp="3ed95p19fb85z" />
            </div>
            <h2 className="text-[18px] font-semibold text-text-primary" data-id="element-1668" data-mp="3jaise95fh5q4">
              Maternal Heart Rate
            </h2>
          </div>
          <button onClick={() => setShowMhrSheet(true)} className="text-primary bg-primary/10 p-2 rounded-full hover:bg-primary/20 transition-colors" aria-label="Log heart rate" data-id="element-1669" data-mp="3o8kd0yii5d3p">
            <Plus size={18} data-id="element-1670" data-mp="owxk7tqpufau" />
          </button>
        </div>

        <MaternalHeartRateWave data-id="element-1671" data-mp="owe66o46sb23" />

        <p className="text-[11px] italic text-text-secondary leading-snug mt-4" data-id="element-1672" data-mp="13lam17chisd9">
          Resting maternal heart rate typically rises to 80–100 bpm in
          pregnancy. Persistent readings above 130 bpm or below 60 bpm should be
          discussed with your doctor.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1673" data-mp="5xs4p84rchxn">
        {/* 4. WEIGHT TRACKER */}
        <div className="bg-white rounded-[24px] p-6 shadow-card border border-border/50 flex flex-col" data-id="element-1674" data-mp="jxl0viz1zqd0">
          <div className="flex justify-between items-center mb-6" data-id="element-1675" data-mp="fw7tv5k85h4i">
            <h2 className="text-[18px] font-semibold text-text-primary" data-id="element-1676" data-mp="fy2f6kzerb94">
              Weight
            </h2>
            <button onClick={() => setShowWeightSheet(true)} className="text-primary bg-primary/10 p-2 rounded-full hover:bg-primary/20 transition-colors" data-id="element-1677" data-mp="1vekssdhho36i">
              <Plus size={18} data-id="element-1678" data-mp="1yx0dhb4qaj1m" />
            </button>
          </div>

          {weightChartData.length > 0 ? <div className="h-[120px] w-full mt-auto" data-id="element-1679" data-mp="1owhmzxins4aq">
              <ResponsiveContainer width="100%" height="100%" data-id="element-1680" data-mp="1oy11bikr859v">
                <LineChart data={weightChartData} data-id="element-1681" data-mp="1tv1ba4kq5s69">
                  <XAxis dataKey="date" hide data-id="element-1682" data-mp="1txnejs0l1lio" />
                  <YAxis hide domain={['dataMin - 2', 'dataMax + 2']} data-id="element-1683" data-mp="2hklvsrwj0xhn" />
                  <Tooltip contentStyle={{
                borderRadius: '12px',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }} data-id="element-1684" data-mp="2mk8h7omidk1j" />
                  <Line type="monotone" dataKey="weight" stroke="#00D4D4" strokeWidth={3} dot={{
                r: 4,
                fill: '#00D4D4'
              }} data-id="element-1685" data-mp="2ct41unsx7x66" />
                </LineChart>
              </ResponsiveContainer>
            </div> : <div className="flex-1 flex flex-col items-center justify-center text-center py-6" data-id="element-1686" data-mp="3a8wwkfg1gv9a">
              <p className="text-[14px] text-text-secondary mb-3" data-id="element-1687" data-mp="364p54ai7517d">
                No weight logged yet
              </p>
              <Button onClick={() => setShowWeightSheet(true)} variant="secondary" className="py-2 px-4 h-auto text-[13px]" data-id="element-1688" data-mp="316nkvm91avub">
                Log Weight
              </Button>
            </div>}
        </div>

        {/* 5. BLOOD PRESSURE LOG */}
        <div className="bg-white rounded-[24px] p-6 shadow-card border border-border/50 flex flex-col" data-id="element-1689" data-mp="2mfogknf8etgj">
          <div className="flex justify-between items-center mb-4" data-id="element-1690" data-mp="2dhcu8xleehtw">
            <h2 className="text-[18px] font-semibold text-text-primary" data-id="element-1691" data-mp="2dgkxvkjz8g2k">
              Blood Pressure
            </h2>
            <button onClick={() => setShowBpSheet(true)} className="text-primary bg-primary/10 p-2 rounded-full hover:bg-primary/20 transition-colors" data-id="element-1692" data-mp="28km4tjnpe9vt">
              <Plus size={18} data-id="element-1693" data-mp="t1er3cm3os2m" />
            </button>
          </div>

          {bpLog.length > 0 ? <div className="space-y-3 overflow-y-auto max-h-[140px] custom-scrollbar pr-2" data-id="element-1694" data-mp="xwkc897oi2pn">
              {bpLog.slice(0, 5).map(entry => {
            const isHigh = entry.systolic > 140 || entry.diastolic > 90;
            return <div key={entry.id} className="flex justify-between items-center p-3 bg-secondary/50 rounded-[12px]" data-id="element-1695" data-mp="a8117lx8vbtv">
                    <div data-id="element-1696" data-mp="j1nwlaxjid2j">
                      <div className="flex items-center" data-id="element-1697" data-mp="j25yoo6vvlid">
                        <span className="text-[16px] font-bold text-text-primary" data-id="element-1698" data-mp="3iom88x6bfuuc">
                          {entry.systolic}/{entry.diastolic}
                        </span>
                        {isHigh && <span className="ml-2 w-2 h-2 rounded-full bg-danger" data-id="element-1699" data-mp="2g2de8ztdhtww" />}
                      </div>
                      <span className="text-[12px] text-text-secondary" data-id="element-1700" data-mp="2b53qrppjnsbp">
                        {new Date(entry.date).toLocaleDateString()}
                      </span>
                    </div>
                    {isHigh && <span className="text-[11px] font-semibold text-danger bg-danger/10 px-2 py-1 rounded-pill" data-id="element-1701" data-mp="2l42q2hof6bm1">
                        High
                      </span>}
                  </div>;
          })}
            </div> : <div className="flex-1 flex flex-col items-center justify-center text-center py-6" data-id="element-1702" data-mp="38qsrphwuw464">
              <p className="text-[14px] text-text-secondary mb-3" data-id="element-1703" data-mp="33t9fenjpy1gt">
                No BP logged yet
              </p>
              <Button onClick={() => setShowBpSheet(true)} variant="secondary" className="py-2 px-4 h-auto text-[13px]" data-id="element-1704" data-mp="3hq60e8d5oops">
                Log BP
              </Button>
            </div>}
        </div>
      </div>

      {/* 6. SYMPTOM DIARY */}
      <div className="bg-white rounded-[24px] p-6 shadow-card border border-border/50" data-id="element-1705" data-mp="3ne9l8q52470o">
        <h2 className="text-[18px] font-semibold text-text-primary mb-4" data-id="element-1706" data-mp="3ngm0gvjfk3a1">
          Symptom Diary
        </h2>
        <div className="flex flex-wrap gap-2 mb-6" data-id="element-1707" data-mp="3dogcdwwau604">
          {SYMPTOMS.map(sym => <button key={sym} onClick={() => handleLogSymptom(sym)} className="px-4 py-2 bg-secondary text-text-primary text-[14px] font-medium rounded-pill hover:bg-primary/10 hover:text-primary transition-colors border border-transparent hover:border-primary/20" data-id="element-1708" data-mp="3dmn1u6ikf1mu">
              + {sym}
            </button>)}
        </div>

        <div className="bg-secondary/50 rounded-[16px] p-4" data-id="element-1709" data-mp="2urot3ngrbqw2">
          <h3 className="text-[13px] font-semibold text-text-secondary uppercase tracking-wider mb-3" data-id="element-1710" data-mp="2zndn7ba0cfkz">
            Today's Symptoms
          </h3>
          {todaySymptoms.length > 0 ? <div className="flex flex-wrap gap-2" data-id="element-1711" data-mp="32vy0s412vp02">
              {todaySymptoms.map((s, i) => <div key={i} className="px-3 py-1.5 bg-white border border-border rounded-pill text-[13px] text-text-primary flex items-center shadow-sm" data-id="element-1712" data-mp="2ow5eqm0hxwyf" data-mpi={i}>
                  {s.symptom}
                  <span className="ml-2 text-[11px] text-text-secondary" data-id="element-1713" data-mp="3hlvv71plpaxp">
                    {new Date(s.timestamp).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit'
              })}
                  </span>
                </div>)}
            </div> : <p className="text-[14px] text-text-secondary" data-id="element-1714" data-mp="4cr54dz2p4l1">
              No symptoms logged today.
            </p>}
        </div>
      </div>

      {/* 7. UPCOMING VISITS & 8. EMERGENCY CONTACT */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1715" data-mp="1ttlam832q8fm">
        <div className="bg-white rounded-[24px] p-6 shadow-card border border-border/50" data-id="element-1716" data-mp="1ts1tkf9lftcs">
          <div className="flex justify-between items-center mb-4" data-id="element-1717" data-mp="1ot7978vz9wqk">
            <h2 className="text-[18px] font-semibold text-text-primary" data-id="element-1718" data-mp="22lewma1dgbcl">
              Upcoming Visit
            </h2>
            <button onClick={() => navigate('/app/appointments')} className="text-primary bg-primary/10 p-2 rounded-full hover:bg-primary/20 transition-colors" data-id="element-1719" data-mp="1ylwj3zbvr2js">
              <Plus size={18} data-id="element-1720" data-mp="15yikzxwg40y3" />
            </button>
          </div>

          {isLoading ? <SkeletonBlock height="80px" borderRadius="16px" data-id="element-1721" data-mp="1jxiaf7rs8jvb" /> : nextAppointment ? <div className="bg-secondary/50 rounded-[16px] p-4" data-id="element-1722" data-mp="1evsx1lh37u42">
              <div className="flex items-start mb-2" data-id="element-1723" data-mp="2vaccy7m0vy2p">
                <Calendar size={18} className="text-primary mt-0.5 mr-2" data-id="element-1724" data-mp="2vd6tho1x0yqu" />
                <div data-id="element-1725" data-mp="2qc13vz3jxrlj">
                  <h3 className="text-[15px] font-semibold text-text-primary" data-id="element-1726" data-mp="2qenc80y9ob4t">
                    {nextAppointment.title}
                  </h3>
                  <p className="text-[13px] text-text-secondary" data-id="element-1727" data-mp="1swwmtnl8mnv8">
                    {new Date(nextAppointment.appointmentDate).toLocaleDateString([], {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                  </p>
                </div>
              </div>
              {nextAppointment.doctorName && <p className="text-[13px] text-text-primary ml-6 mt-1" data-id="element-1728" data-mp="1e7qpk55yn7pz">
                  Dr. {nextAppointment.doctorName}
                </p>}
            </div> : <div className="text-center py-4" data-id="element-1729" data-mp="ljurox4nn9rm">
              <p className="text-[14px] text-text-secondary mb-3" data-id="element-1730" data-mp="qj7hnqqtowyf">
                No upcoming visits
              </p>
              <Button onClick={() => navigate('/app/appointments')} variant="secondary" className="py-2 px-4 h-auto text-[13px]" data-id="element-1731" data-mp="j07v5c82epau">
                Schedule Visit
              </Button>
            </div>}
        </div>

        <div className="bg-white rounded-[24px] p-6 shadow-card border border-border/50" data-id="element-1732" data-mp="50q7j06i9i32">
          <h2 className="text-[18px] font-semibold text-text-primary mb-4" data-id="element-1733" data-mp="1lwe400taa3">
            Emergency Contact
          </h2>
          {nextAppointment?.doctorName ? <div className="bg-danger/5 border border-danger/10 rounded-[16px] p-4 flex items-center justify-between" data-id="element-1734" data-mp="3rdoomzhs707z">
              <div data-id="element-1735" data-mp="3hhljrlvjonhc">
                <h3 className="text-[15px] font-semibold text-text-primary mb-1" data-id="element-1736" data-mp="3hid769eznewj">
                  Dr. {nextAppointment.doctorName}
                </h3>
                {nextAppointment.location && <p className="text-[13px] text-text-secondary" data-id="element-1737" data-mp="38gni3a8kws1x">
                    {nextAppointment.location}
                  </p>}
              </div>
              <a href="tel:911" className="w-10 h-10 bg-danger text-white rounded-full flex items-center justify-center shadow-md hover:bg-danger/90 transition-colors" data-id="element-1738" data-mp="1gfcm9ogt4pl7">
                <Phone size={18} data-id="element-1739" data-mp="17miskm0m5nnt" />
              </a>
            </div> : <div className="text-center py-4" data-id="element-1740" data-mp="sq1jk1fwcl4f">
              <p className="text-[14px] text-text-secondary" data-id="element-1741" data-mp="sp047krulcgw">
                No emergency contact set — add one in Appointments
              </p>
            </div>}
        </div>
      </div>

      {/* BOTTOM SHEETS */}
      <AnimatePresence data-id="element-1742" data-mp="jr6a8ghk2uww">
        {showWeightSheet && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowWeightSheet(false)} className="fixed inset-0 bg-black/40 z-[100]" data-id="element-1743" data-mp="ooq2ikb3987u" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-[24px] p-6 z-[101] pb-[env(safe-area-inset-bottom)] md:max-w-[480px] md:mx-auto md:rounded-[24px] md:bottom-6 md:left-6 md:right-6" data-id="element-1744" data-mp="2o7l1nigthjzr">
              <div className="flex justify-between items-center mb-6" data-id="element-1745" data-mp="3gvql81w4x2y0">
                <h2 className="text-[20px] font-semibold text-text-primary" data-id="element-1746" data-mp="3gwiakuz524fu">
                  Log Weight
                </h2>
                <button onClick={() => setShowWeightSheet(false)} data-id="element-1747" data-mp="1mhpg4z9zrjph">
                  <X size={24} className="text-text-secondary" data-id="element-1748" data-mp="1mjil8a4yhvvd" />
                </button>
              </div>
              <div className="space-y-4 mb-6" data-id="element-1749" data-mp="3lok4ciexy01j">
                <div data-id="element-1750" data-mp="3qkqzrh9pnt58">
                  <label className="block text-[13px] font-medium text-text-primary mb-2" data-id="element-1751" data-mp="3qmbw9cfl5m5q">
                    Weight (kg)
                  </label>
                  <InputField type="number" placeholder="e.g. 65" value={weightInput} onChange={e => setWeightInput(e.target.value)} data-id="element-1752" data-mp="4ec5fs4s8xx5" />
                </div>
                <div data-id="element-1753" data-mp="ddftlait43ii">
                  <label className="block text-[13px] font-medium text-text-primary mb-2" data-id="element-1754" data-mp="dcxpxstten0k">
                    Date
                  </label>
                  <input type="date" value={weightDate} onChange={e => setWeightDate(e.target.value)} className="w-full h-[52px] rounded-[12px] border border-border px-4 focus:border-primary outline-none" data-id="element-1755" data-mp="n53cuo57me8w" />
                </div>
              </div>
              <Button onClick={handleSaveWeight} disabled={!weightInput} data-id="element-1756" data-mp="2glg41g8uvx7d">
                Save Entry
              </Button>
            </motion.div>
          </>}

        {showBpSheet && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowBpSheet(false)} className="fixed inset-0 bg-black/40 z-[100]" data-id="element-1757" data-mp="27opabahs0ufy" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-[24px] p-6 z-[101] pb-[env(safe-area-inset-bottom)] md:max-w-[480px] md:mx-auto md:rounded-[24px] md:bottom-6 md:left-6 md:right-6" data-id="element-1758" data-mp="2qkfen4d6m2x0">
              <div className="flex justify-between items-center mb-6" data-id="element-1759" data-mp="2dnur7zig142k">
                <h2 className="text-[20px] font-semibold text-text-primary" data-id="element-1760" data-mp="2hon370ip9n2j">
                  Log Blood Pressure
                </h2>
                <button onClick={() => setShowBpSheet(false)} data-id="element-1761" data-mp="35fanymdps76i">
                  <X size={24} className="text-text-secondary" data-id="element-1762" data-mp="3adc7f8f1y8ii" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-1763" data-mp="2wdt6azknrxfp">
                <div data-id="element-1764" data-mp="2wfnjfrpoul98">
                  <label className="block text-[13px] font-medium text-text-primary mb-2" data-id="element-1765" data-mp="31cmag1e22c37">
                    Systolic
                  </label>
                  <InputField type="number" placeholder="120" value={bpSysInput} onChange={e => setBpSysInput(e.target.value)} data-id="element-1766" data-mp="1kxl2kuthqg2g" />
                </div>
                <div data-id="element-1767" data-mp="1devd2nu73fu2">
                  <label className="block text-[13px] font-medium text-text-primary mb-2" data-id="element-1768" data-mp="1dfejp8eeahss">
                    Diastolic
                  </label>
                  <InputField type="number" placeholder="80" value={bpDiaInput} onChange={e => setBpDiaInput(e.target.value)} data-id="element-1769" data-mp="1rduzdou8jin2" />
                </div>
              </div>
              <div className="mb-6" data-id="element-1770" data-mp="trxddhgncv6d">
                <label className="block text-[13px] font-medium text-text-primary mb-2" data-id="element-1771" data-mp="tot0zwt12t4m">
                  Date
                </label>
                <input type="date" value={bpDate} onChange={e => setBpDate(e.target.value)} className="w-full h-[52px] rounded-[12px] border border-border px-4 focus:border-primary outline-none" data-id="element-1772" data-mp="13pbp53rmkvsc" />
              </div>
              <Button onClick={handleSaveBp} disabled={!bpSysInput || !bpDiaInput} data-id="element-1773" data-mp="3569utyo6nmf1">
                Save Entry
              </Button>
            </motion.div>
          </>}

        {showMhrSheet && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowMhrSheet(false)} className="fixed inset-0 bg-black/40 z-[100]" data-id="element-1774" data-mp="2vdlzz9grjsiy" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-[24px] p-6 z-[101] pb-[env(safe-area-inset-bottom)] md:max-w-[480px] md:mx-auto md:rounded-[24px] md:bottom-6 md:left-6 md:right-6" data-id="element-1775" data-mp="3e54wqyk72acj">
              <div className="flex justify-between items-center mb-6" data-id="element-1776" data-mp="n7abegtdz262">
                <h2 className="text-[20px] font-semibold text-text-primary" data-id="element-1777" data-mp="rys1g9tvfic9">
                  Log Heart Rate
                </h2>
                <button onClick={() => setShowMhrSheet(false)} data-id="element-1778" data-mp="wx3c2mrzpwvj">
                  <X size={24} className="text-text-secondary" data-id="element-1779" data-mp="11wzfajsv5ka1" />
                </button>
              </div>
              <div className="space-y-4 mb-6" data-id="element-1780" data-mp="15yb80y71almg">
                <div data-id="element-1781" data-mp="15zcndev31u9z">
                  <label className="block text-[13px] font-medium text-text-primary mb-2" data-id="element-1782" data-mp="15x0510lexhwe">
                    Heart Rate (bpm)
                  </label>
                  <InputField type="number" placeholder="e.g. 82" value={mhrInput} onChange={e => setMhrInput(e.target.value)} data-id="element-1783" data-mp="37r9370oa9qak" />
                </div>
                <div className="grid grid-cols-2 gap-4" data-id="element-1784" data-mp="jwn5w5ne0911">
                  <div data-id="element-1785" data-mp="ex0s9v3ncqrp">
                    <label className="block text-[13px] font-medium text-text-primary mb-2" data-id="element-1786" data-mp="exsh8n2qnq90">
                      Date
                    </label>
                    <input type="date" value={mhrDate} onChange={e => setMhrDate(e.target.value)} className="w-full h-[52px] rounded-[12px] border border-border px-4 focus:border-primary outline-none" data-id="element-1787" data-mp="nw4e36u77k9w" />
                  </div>
                  <div data-id="element-1788" data-mp="1bl6u6a96p52a">
                    <label className="block text-[13px] font-medium text-text-primary mb-2" data-id="element-1789" data-mp="1bnt065gffcie">
                      Time
                    </label>
                    <input type="time" value={mhrTime} onChange={e => setMhrTime(e.target.value)} className="w-full h-[52px] rounded-[12px] border border-border px-4 focus:border-primary outline-none" data-id="element-1790" data-mp="1ll7fg1qm5v94" />
                  </div>
                </div>
              </div>
              <Button onClick={handleSaveMhr} disabled={!mhrInput} data-id="element-1791" data-mp="1q96uc31l49vk">
                Save Entry
              </Button>
            </motion.div>
          </>}
      </AnimatePresence>
    </main>;
}